import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";
import { caseTransformingAxios } from "@shared/v2/caseTransformingAxios";
import { isPlace } from "@shared/Utilities";
import formatters from "../../shared/formatters";
import { sortBy } from "lodash";
import { uploadAvatar } from "../contactsReducer";

export const addressModalInitialState = {
  show: false,
  initAddress: null,
  title: null,
  onSaveTxt: null,
  hideAddressType: false,
  usedAddresses: [],
};

export const invalidAddressModalInitialState = {
  show: false,
  address: null,
};

export const verifyAddressModalInitialState = {
  show: false,
  originalAddress: null,
  suggestedAddress: null,
};

export const initialState = {
  counts: {
    Leads: {},
    People: {},
    Tasks: {},
    Transactions: {},
  },
  currentMetaUser: null,
  currentSuperUser: null,
  currentUser: null,
  env: null,
  featureFlags: null,
  accountProducts: {},
  isMobileMenuOpen: false,
  sidebar: null,
  submenu: null,
  selectedMenuOption: { name: "" },
  theme: null,
  support: null,
  accountCallRecordingLoading: false,
  appLinksLoading: false,
  appLinks: [],
  addressModal: addressModalInitialState,
  invalidAddressModal: invalidAddressModalInitialState,
  verifyAddressModal: verifyAddressModalInitialState,
  stateOptions: null,
  stateOptionsLoading: false,
};

const transformCounts = (counts) =>
  Object.keys(counts).reduce((acc, key) => ({ ...acc, [key]: formatters.number(counts[key]) }), {
    lastUpdate: Date.now(),
  });

const getAppLinks = createAsyncThunk("layout/getAppLinks", (_, thunkApi) =>
  caseTransformingAxios.get("/api/v4/app_links", { signal: thunkApi.signal }).then(({ data }) => data),
);

const STATE_OPTIONS_CONFIG = {
  unitedStates: { label: "United States", abbreviation: "US", country: "US" },
  canada: { label: "Canada", abbreviation: "CA", country: "CA" },
  armedForces: { label: "Armed Forces", country: "US" },
};

const STATE_OPTIONS_ORDER = ["unitedStates", "canada", "armedForces"];

const transformStateOptions = (stateOptions) =>
  Object.entries(stateOptions)
    .sort(([a], [b]) => STATE_OPTIONS_ORDER.indexOf(a) - STATE_OPTIONS_ORDER.indexOf(b))
    .map(([key, states]) => {
      const config = STATE_OPTIONS_CONFIG[key] || { label: key, country: "US" };
      return {
        label: config.label,
        ...(config.abbreviation && { abbreviation: config.abbreviation }),
        options: states.map(({ name, code }) => ({
          label: `${name.trim()} (${code})`,
          value: code,
          country: config.country,
        })),
      };
    });

const getStateOptions = createAsyncThunk("layout/getStateOptions", (_, thunkApi) =>
  caseTransformingAxios
    .get("/api/v4/contact_people/form_options", { signal: thunkApi.signal })
    .then(({ data }) => transformStateOptions(data.stateOptions)),
);

const updateAccountRecordingSettings = createAsyncThunk(
  "phoneSettings/updateAccountRecordingSettings",
  (callRecordingAccountSettings, thunkApi) =>
    caseTransformingAxios
      .post(
        "/api/v4/call_recording/account_settings/upsert",
        {
          callRecordingAccountSettings,
        },
        { signal: thunkApi.signal },
      )
      .then(({ data }) => data),
);

export const layoutSlice = createSlice({
  name: "layout",
  initialState,
  reducers: {
    addPeopleSmartFilterOption: (state, { payload: { option } }) => {
      state.sidebar.menu.forEach((menu) => {
        const smartFilterIndex =
          menu.name !== "Transactions" ? menu.options?.findIndex((o) => o.name === "Smart Filters") : null;
        const smartFilterOptions = menu.options?.[smartFilterIndex]?.options;
        if (smartFilterOptions) {
          menu.options[smartFilterIndex].options = sortBy(
            [...smartFilterOptions, option],
            [(o) => Boolean(o.deletePath), "name"],
          );
        }
      });
    },
    removeSmartFilterOption: (state, { payload: { option } }) => {
      state.sidebar.menu.forEach((menu) => {
        const smartFilterOptions = menu.options?.find((o) => o.name === "Smart Filters")?.options;
        if (smartFilterOptions) {
          const optionIndex = smartFilterOptions.findIndex(
            (o) => o.id === option.id && o.name === option.name,
          );
          if (optionIndex > -1) {
            smartFilterOptions.splice(optionIndex, 1);
          }
        }
      });
    },
    setIsSidebarOpen: (state, { payload }) => {
      state.sidebar.isOpen = payload;
    },
    setIsSubmenuOpen: (state, { payload }) => {
      state.submenu = payload;
    },
    setSelectedMenuOption: (state, { payload }) => {
      state.selectedMenuOption = payload;
    },
    toggleMobileMenu: (state, { payload }) => {
      state.isMobileMenuOpen = typeof payload === "boolean" ? payload : !state.isMobileMenuOpen;
    },
    updateLeadCounts: (state, { payload }) => {
      state.counts = { ...state.counts, Leads: transformCounts(payload) };
    },
    updateLeadPondsCounts: (state, { payload }) => {
      state.counts = { ...state.counts, "Lead Ponds": transformCounts(payload) };
    },
    updatePeopleCounts: (state, { payload }) => {
      state.counts = { ...state.counts, People: transformCounts(payload) };
    },
    updateTaskCounts: (state, { payload }) => {
      state.counts = { ...state.counts, Tasks: transformCounts(payload) };
    },
    updateTransactionCounts: (state, { payload }) => {
      const withAllData = { ...payload, all_count: 0 };
      Object.keys(payload).forEach((key) => {
        // draft not related to statuses. So we need to skip it to remove double counting of
        // any transaction that is a draft, as they _also_ are counted in the statuses
        if (key === "drafts_count") return;
        const value = payload[key];
        withAllData.all_count += value;
      });
      state.counts = { ...state.counts, Transactions: transformCounts(withAllData) };
    },
    openAddressModal: (state, { payload }) => {
      state.addressModal = {
        ...addressModalInitialState,
        ...payload,
        show: true,
      };
    },
    closeAddressModal: (state) => {
      state.addressModal = addressModalInitialState;
    },
    updateAddressModal: (state, { payload }) => {
      state.addressModal = {
        ...state.addressModal,
        ...payload,
      };
    },
    openInvalidAddressModal: (state, { payload }) => {
      state.invalidAddressModal = {
        ...invalidAddressModalInitialState,
        ...payload,
        show: true,
      };
    },
    closeInvalidAddressModal: (state) => {
      state.invalidAddressModal = invalidAddressModalInitialState;
    },
    openVerifyAddressModal: (state, { payload }) => {
      state.verifyAddressModal = {
        ...verifyAddressModalInitialState,
        ...payload,
        show: true,
      };
    },
    closeVerifyAddressModal: (state) => {
      state.verifyAddressModal = verifyAddressModalInitialState;
    },
    setConnectServiceActive: (state) => {
      // Create the key rather than guarding on it: preloadedState replaces
      // initialState, so a payload without account_products would otherwise
      // drop this patch silently.
      state.accountProducts = { ...state.accountProducts, connectService: true };
    },
    updateAccountCountry: (state, { payload }) => {
      if (state.currentUser?.account) {
        state.currentUser.account.country = payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(uploadAvatar.fulfilled, (state, { payload }) => {
      if (String(state.currentUser.person.id) !== payload.data.id) return;
      state.currentUser.avatar = payload.data.attributes.avatarUrl;
    });
    builder
      .addCase(updateAccountRecordingSettings.pending, (state) => {
        state.accountCallRecordingLoading = true;
      })
      .addCase(updateAccountRecordingSettings.fulfilled, (state, action) => {
        state.currentUser.account.callRecordingSettings = action.payload;
        state.accountCallRecordingLoading = false;
      })
      .addCase(updateAccountRecordingSettings.rejected, (state) => {
        state.accountCallRecordingLoading = false;
      });
    builder
      .addCase(getAppLinks.pending, (state) => {
        state.appLinksLoading = true;
      })
      .addCase(getAppLinks.fulfilled, (state, action) => {
        state.appLinks = action.payload;
        state.appLinksLoading = false;
      })
      .addCase(getAppLinks.rejected, (state) => {
        state.appLinksLoading = false;
      });
    builder
      .addCase(getStateOptions.pending, (state) => {
        state.stateOptionsLoading = true;
      })
      .addCase(getStateOptions.fulfilled, (state, action) => {
        state.stateOptions = action.payload;
        state.stateOptionsLoading = false;
      })
      .addCase(getStateOptions.rejected, (state) => {
        state.stateOptionsLoading = false;
      });
  },
});

export { updateAccountRecordingSettings, getAppLinks, getStateOptions };

export const {
  addPeopleSmartFilterOption,
  removeSmartFilterOption,
  setIsSubmenuOpen,
  setIsSidebarOpen,
  setSelectedMenuOption,
  toggleMobileMenu,
  updateAccountCountry,
  updateLeadCounts,
  updateLeadPondsCounts,
  updatePeopleCounts,
  updateTaskCounts,
  updateTransactionCounts,
  openAddressModal,
  closeAddressModal,
  updateAddressModal,
  openInvalidAddressModal,
  closeInvalidAddressModal,
  openVerifyAddressModal,
  closeVerifyAddressModal,
  setConnectServiceActive,
} = layoutSlice.actions;

export default layoutSlice.reducer;

export const usePlaceSelector = () => useSelector(({ layout }) => isPlace(layout.theme.name));
